/**
 * Release tour — Emirates ID.
 *
 * Copy is DERIVED from `VITE_EID_USE_AGENT` so it always matches the path that actually
 * shipped in this build — there is no hand-maintained "which build does what" assumption to
 * drift (an earlier version of this file asserted the button was "Dev only, never Prod"; the
 * flag was later flipped on in prod via `prod-web.yml`, which silently invalidated that copy).
 *
 * The two EID paths are mutually exclusive in `personal-details-tab.tsx`:
 *   • flag ON  (Prod, PROD-1289): button read — the passive SignalR listener is DISABLED and a
 *     "Read Emirates ID" button is rendered. Step 1 anchors that button.
 *   • flag OFF (Dev,  PROD-727):  passive SignalR desk-scanner auto-fill — no button rendered.
 *     Step 1 anchors the "ID card" section.
 * Because the button only renders when the flag is on, branching the step here keeps every
 * build's steps pointing only at targets that actually exist in the DOM (Joyride would skip /
 * warn on a missing target otherwise).
 *
 * Anchors are real `data-tour` attributes: `patient-eid-agent-button` on the button wrapper
 * (flag-on only) and `patient-id-card` / `patient-personal-details` / `patient-identity-
 * verification` on the `FormSection`s via its `dataTour` prop.
 *
 * Mount at the top of `PersonalDetailsTab` (the default landing tab on both
 * `/patients/new` and `/patients/edit/:patientId`):
 *   const tour = useReleaseTour(EMIRATES_ID_TOUR, { userRole: auth?.role });
 *   return <FormProvider ...><ReleaseTour {...tour} />...</FormProvider>;
 */
import type { ReleaseTourConfig, ReleaseTourStep } from '../release-tour-types';
import { registerReleaseTour } from '../release-tour-registry';

const useAgent = import.meta.env.VITE_EID_USE_AGENT === 'true';

// Step 1 is the only step that differs by path — the button (agent) vs. the passive scan.
// Steps 2–3 describe the fields filling in + the 784 format, which are identical either way.
const firstStep: ReleaseTourStep = useAgent
  ? {
      target: '[data-tour="patient-eid-agent-button"]',
      title: 'Read the Emirates ID in one click',
      content:
        'Place the patient’s Emirates ID on the reader and click Read Emirates ID — Scrips reads the card and fills the form for you. No typing. (If it says the reader agent isn’t running on this PC, that just needs installing once.)',
      placement: 'bottom',
    }
  : {
      target: '[data-tour="patient-id-card-section"]',
      title: 'No typing required',
      content:
        'Scan the patient’s Emirates ID at your existing desk reader — Scrips picks it up the moment the card is read. No button to click, nothing to configure.',
      placement: 'bottom',
    };

export const EMIRATES_ID_TOUR: ReleaseTourConfig = {
  id: 'emirates-id-v1',
  name: 'Emirates ID',
  enabled: true,
  steps: [
    firstStep,
    {
      target: '[data-tour="patient-personal-details-section"]',
      title: 'The form fills itself',
      content:
        'Name, date of birth, gender, and photo populate automatically from the card — your front desk checks it, instead of typing it.',
      placement: 'top',
    },
    {
      target: '[data-tour="patient-identity-verification-section"]',
      title: 'The ID number, formatted for you',
      content:
        'The Emirates ID number fills in and displays in the standard 784 format automatically — and it works the same way again later, when a patient signs a consent form. This tour won’t show again.',
      placement: 'top',
    },
  ],
};

registerReleaseTour(EMIRATES_ID_TOUR);
